// The exercise library's search, filters, counts, order and groups (pages/ExerciseHub.jsx). Exercises are the API's
// { id, attributes } entries
import { EXERCISE_REGIONS, regionOf } from './exerciseRegions';

export const normalize = (str) => (str || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** A search's words, each looked for on its own and in any order: "supino halter" finds "Supino Reto com Halteres" */
export const searchWords = (term) => normalize(term).split(/\s+/).filter(Boolean);

// Every word has to be in the name, Portuguese or English
const matchesWords = (attrs, words) => {
  if (!words.length) return true;
  const names = [normalize(attrs.titlePt), normalize(attrs.title)];
  return words.every((word) => names.some((name) => name.includes(word)));
};

/**
 * filters: { words, regions, equipment }. Body parts combine with "or", the search, parts and equipment with "and".
 * skip leaves one of them out: a count beside a filter says what picking it would show with the others on
 */
export const matchesFilters = (attrs, { words = [], regions = [], equipment = '' }, skip = '') =>
  matchesWords(attrs, words) &&
  (skip === 'regions' || !regions.length || regions.includes(regionOf(attrs.muscleGroup))) &&
  (skip === 'equipment' || !equipment || attrs.equipmentCategory === equipment);

export const regionCounts = (exercises, filters) => {
  const counts = Object.fromEntries(EXERCISE_REGIONS.map(({ key }) => [key, 0]));
  exercises.forEach(({ attributes }) => {
    const region = regionOf(attributes.muscleGroup);
    if (region && matchesFilters(attributes, filters, 'regions')) counts[region] += 1;
  });
  return counts;
};

export const equipmentCounts = (exercises, filters) => {
  const counts = {};
  exercises.forEach(({ attributes }) => {
    const key = attributes.equipmentCategory;
    if (key) counts[key] = (counts[key] || 0) + (matchesFilters(attributes, filters, 'equipment') ? 1 : 0);
  });
  return counts;
};

/** The library's equipment in the order people look for it: the one with the most exercises first */
export const equipmentOrder = (exercises) => {
  const totals = equipmentCounts(exercises, {});
  return Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
};

/** Most used first (the library's own priority, the common movements), then the name; or A–Z */
export const sortExercises = (exercises, sort, titleOf, locale) => {
  const byName = (a, b) => titleOf(a.attributes).localeCompare(titleOf(b.attributes), locale);
  const byUse = (a, b) => (b.attributes.priority || 0) - (a.attributes.priority || 0) || byName(a, b);
  return [...exercises].sort(sort === 'az' ? byName : byUse);
};

const MUSCLE_ORDER = EXERCISE_REGIONS.flatMap(({ muscles }) => muscles);
const rank = (list, value) => (list.indexOf(value) < 0 ? list.length : list.indexOf(value));
const regionRank = (muscle) => rank(EXERCISE_REGIONS.map(({ key }) => key), regionOf(muscle));

/**
 * The list grouped by main muscle, in the body's order: a part's bigger groups first (Costas before Lombar). Each
 * group keeps the list's own order
 */
export const groupByMuscle = (exercises) => {
  const groups = new Map();
  exercises.forEach((exercise) => {
    const muscle = exercise.attributes.muscleGroup || 'other';
    if (!groups.has(muscle)) groups.set(muscle, []);
    groups.get(muscle).push(exercise);
  });
  return [...groups.entries()]
    .map(([muscle, items]) => ({ muscle, exercises: items }))
    .sort(
      (a, b) =>
        regionRank(a.muscle) - regionRank(b.muscle) ||
        b.exercises.length - a.exercises.length ||
        rank(MUSCLE_ORDER, a.muscle) - rank(MUSCLE_ORDER, b.muscle)
    );
};

/** The steps of an exercise, in the language of the site (English when there is no translation), without numbers */
export const stepsOf = (attrs, isPt) =>
  ((isPt && attrs.localisedInstructions?.pt) || attrs.localisedInstructions?.en || '')
    .split('\n')
    .map((line) => line.replace(/^\s*\d+[.)]\s*/, '').trim())
    .filter(Boolean);

/** "a", "a e b", "a, b e c" */
export const joinList = (items, and) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
